/**
 * picks-core team image folders for My Games cards.
 *
 * Port of `apps/daily-reveal/src/utils/teamAssetFolder.ts` so a Daily Reveal tile in the
 * lobby resolves the same artwork the game itself shows — most visibly NFL, where every
 * card is a single player and the art is the position-cut jersey in `nfl/jerseys-position/`
 * rather than the plain team jersey (DKPLAT-778).
 *
 * The daily-reveal copy keeps the contest folder in module state, set once by
 * `DailyRevealStyleProvider`. The lobby renders cards from several contests, sports and
 * League IP settings at once, so here the folder is a parameter instead of ambient state.
 */
import { normalizeMlbBlobTeamCode } from './mlbBlobTeamCode';

const STORAGE_ACCOUNT = import.meta.env.VITE_BLOB_STORAGE_ACCOUNT_NAME || 'draftkingsdev';
const CONTAINER = import.meta.env.VITE_BLOB_STORAGE_CONTAINER || 'picks-core';
const CDN_BASE_URL = `https://${STORAGE_ACCOUNT}.blob.core.windows.net/${CONTAINER}`;

export type TeamAssetFolderId =
  | 'teams'
  | 'jerseys'
  | 'jerseys-position'
  | 'team-jerseys'
  | 'helmet';

/**
 * Positions `jerseys-position` actually has artwork for.
 *
 * The folder is keyed `{TEAM}_{POSITION}.png` (e.g. `nfl/jerseys-position/ARI_QB.png`) and
 * holds these four positions only. A kicker, a defence, or any defensive player who scores
 * has no file there, so the URL builder falls back to the plain team jersey.
 */
const JERSEY_POSITIONS = ['QB', 'RB', 'TE', 'WR'] as const;
type JerseyPosition = (typeof JERSEY_POSITIONS)[number];

/** The `jerseys-position` position for a roster position string, or null when unsupported. */
export function resolveJerseyPosition(position: string | null | undefined): JerseyPosition | null {
  if (!position) return null;
  // Roster values are occasionally compound ("WR/RB"); the first token is the primary
  // position and is what the artwork is keyed on.
  const primary = position
    .trim()
    .toUpperCase()
    .split(/[^A-Z]+/)
    .filter(Boolean)[0];
  if (!primary) return null;
  return (JERSEY_POSITIONS as readonly string[]).includes(primary)
    ? (primary as JerseyPosition)
    : null;
}

const FOLDERS_BY_SPORT: Record<string, TeamAssetFolderId[]> = {
  nfl: ['teams', 'jerseys', 'jerseys-position', 'helmet'],
  nba: ['teams', 'team-jerseys'],
  /*
   * WNBA has generic jerseys only. No `wnba/teams/` logo set has been supplied, so listing
   * `teams` here would let the League IP branch below resolve a folder that 404s. Add it
   * when licensed logos land.
   */
  wnba: ['team-jerseys'],
  mlb: ['teams', 'jerseys'],
  ncaamb: ['teams'],
  ncaafb: ['helmet', 'jerseys', 'teams'],
  nhl: ['teams'],
};

/** Team-logo file extensions — mirrors `getFileExtForSport` in `@low6-pickem/sports-config`. */
const SPORT_FILE_EXTENSIONS: Record<string, string> = {
  mlb: 'png',
  nba: 'png',
  wnba: 'png',
  ncaamb: 'png',
  nfl: 'png',
};

function fileExtForSport(sport: string): string {
  return SPORT_FILE_EXTENSIONS[sport.toLowerCase()] || 'svg';
}

export function isTeamAssetFolderId(value: unknown): value is TeamAssetFolderId {
  return (
    value === 'teams' ||
    value === 'jerseys' ||
    value === 'jerseys-position' ||
    value === 'team-jerseys' ||
    value === 'helmet'
  );
}

/** Contest folder override from the Daily Reveal contest's `style_config.teamAssetFolder`. */
export function readTeamAssetFolder(
  styleConfig?: Record<string, unknown> | null
): TeamAssetFolderId | null {
  const raw = styleConfig?.teamAssetFolder;
  return isTeamAssetFolderId(raw) ? raw : null;
}

/**
 * Daily Reveal's house default per sport, used ahead of the generic non-IP jersey folder.
 *
 * Every Daily Reveal card shows a single player, so NFL wants the position-cut jersey rather
 * than the plain team one. Mirrors `DAILY_REVEAL_DEFAULT_FOLDER` in daily-reveal and
 * `DEFAULT_FOLDER_BY_GAME_TYPE.daily_reveal` in Admin `asset-folders.ts`.
 */
const DAILY_REVEAL_DEFAULT_FOLDER: Record<string, TeamAssetFolderId> = {
  nfl: 'jerseys-position',
};

export function defaultTeamAssetFolder(sport: string, leagueIp: boolean): TeamAssetFolderId {
  const sportLower = sport.trim().toLowerCase();
  const available = FOLDERS_BY_SPORT[sportLower] ?? ['teams'];

  if (leagueIp) {
    if (sportLower === 'ncaafb' && available.includes('helmet')) return 'helmet';
    return available.includes('teams') ? 'teams' : available[0];
  }

  const gameDefault = DAILY_REVEAL_DEFAULT_FOLDER[sportLower];
  if (gameDefault && available.includes(gameDefault)) return gameDefault;

  if ((sportLower === 'nba' || sportLower === 'wnba') && available.includes('team-jerseys'))
    return 'team-jerseys';
  if (available.includes('jerseys')) return 'jerseys';
  return available[0] ?? 'teams';
}

/** Effective folder for a card: contest override (when the sport has it) → League IP default. */
export function resolveEffectiveTeamAssetFolder(
  sport: string | null | undefined,
  leagueIp: boolean,
  contestFolder: TeamAssetFolderId | null = null
): TeamAssetFolderId {
  const sportKey = sport?.trim() || 'MLB';
  if (contestFolder) {
    const available = FOLDERS_BY_SPORT[sportKey.toLowerCase()] ?? ['teams'];
    if (available.includes(contestFolder)) return contestFolder;
  }
  return defaultTeamAssetFolder(sportKey, leagueIp);
}

function resolveBlobAbbr(teamAbbr: string, sport: string): string {
  const sportLower = sport.trim().toLowerCase();
  if (sportLower === 'mlb') {
    return normalizeMlbBlobTeamCode(teamAbbr) || teamAbbr.trim().toUpperCase();
  }
  return teamAbbr.trim().toUpperCase();
}

/** Build a CDN URL for a team abbreviation in the selected asset folder. */
export function generateTeamAssetUrl(
  teamAbbr: string,
  sport: string,
  folder: TeamAssetFolderId,
  position?: string | null
): string {
  const sportLower = sport.trim().toLowerCase();
  const code = resolveBlobAbbr(teamAbbr, sportLower);
  if (!code) return '';

  switch (folder) {
    case 'jerseys':
      return `${CDN_BASE_URL}/${sportLower}/jerseys/${code}.png`;
    case 'jerseys-position': {
      const pos = resolveJerseyPosition(position);
      if (!pos) return `${CDN_BASE_URL}/${sportLower}/jerseys/${code}.png`;
      return `${CDN_BASE_URL}/${sportLower}/jerseys-position/${code}_${pos}.png`;
    }
    case 'team-jerseys':
      return `${CDN_BASE_URL}/${sportLower}/team-jerseys/${code}.png`;
    // Sport-relative: NCAAFB still resolves to `ncaafb/helmet/`, and NFL reaches its own
    // `nfl/helmet/` rather than borrowing college artwork.
    case 'helmet':
      return `${CDN_BASE_URL}/${sportLower}/helmet/${code}.png`;
    case 'teams':
    default:
      return `${CDN_BASE_URL}/${sportLower}/teams/${code}.${fileExtForSport(sportLower)}`;
  }
}

/**
 * Non-IP / jersey-style player art: prefer jersey folders; never use `teams` logos as the
 * player jersey substitute when the contest folder is logos-only.
 */
export function resolveJerseyStyleTeamUrl(
  teamCode: string | null | undefined,
  sport: string | null | undefined = 'MLB',
  position?: string | null,
  contestFolder: TeamAssetFolderId | null = null,
  leagueIp: boolean = false
): string {
  if (!teamCode || typeof teamCode !== 'string' || !sport) return '';
  const effective = resolveEffectiveTeamAssetFolder(sport, leagueIp, contestFolder);
  const folder = effective === 'teams' ? defaultTeamAssetFolder(sport, false) : effective;
  return generateTeamAssetUrl(teamCode, sport, folder, position);
}
